import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { trackEvent } from '../../utils/trackEvent';
import type { BillingPlansResponse, Subscription, CheckoutResponse, PortalResponse, BillingUsage } from '../../types';

// Fetch available products and subscriptions
export function usePlans() {
  return useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: async () => {
      const { data } = await apiClient.get<BillingPlansResponse>('/api/v1/billing/plans');
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - plans rarely change
  });
}

// Fetch current subscription status
export function useSubscription() {
  return useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: async () => {
      const { data } = await apiClient.get<Subscription>('/api/v1/billing/subscription');
      return data;
    },
    staleTime: 30 * 1000,
  });
}

// Fetch billing usage
export function useBillingUsage(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['billing', 'usage'],
    queryFn: async () => {
      const { data } = await apiClient.get<BillingUsage>('/api/v1/billing/usage');
      return data;
    },
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
}

// Create checkout session for Planner subscription
export function useCheckout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (priceId: string) => {
      const { data } = await apiClient.post<CheckoutResponse>('/api/v1/billing/checkout', {
        price_id: priceId,
      });
      return data;
    },
    onSuccess: (data) => {
      if (data.checkout_url) {
        trackEvent('stripe_checkout_opened', { type: 'planner_subscription' });
        window.location.href = data.checkout_url;
      } else if (data.redirect_url) {
        queryClient.invalidateQueries({ queryKey: ['billing'] });
        window.location.href = data.redirect_url;
      }
    },
  });
}

// Purchase a single invite (one-time payment)
export function usePurchaseInvite() {
  return useMutation({
    mutationFn: async ({ inviteId, product, returnTo }: { inviteId: number; product?: string; returnTo?: string }) => {
      const { data } = await apiClient.post<CheckoutResponse>('/api/v1/billing/purchase-invite', {
        invite_id: inviteId,
        product: product || 'per_invite',
        ...(returnTo ? { return_to: returnTo } : {}),
      });
      return data;
    },
    onSuccess: (data) => {
      if (data.checkout_url) {
        trackEvent('stripe_checkout_opened', { type: 'per_invite' });
        window.location.href = data.checkout_url;
      }
    },
  });
}

// Get customer portal URL
export function usePortal() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<PortalResponse>('/api/v1/billing/portal');
      return data;
    },
    onSuccess: (data) => {
      window.location.href = data.portal_url;
    },
  });
}

// Cancel subscription
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/api/v1/billing/cancel');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
  });
}

// Resume subscription (if on grace period)
export function useResumeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/api/v1/billing/resume');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
  });
}

// Purchase the table plan add-on (one-time)
export function usePurchaseTablePlan() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<CheckoutResponse>('/api/v1/billing/purchase-table-plan', {
        return_origin: window.location.origin,
      });
      return data;
    },
    onSuccess: (data) => {
      if (data.checkout_url) {
        trackEvent('stripe_checkout_opened', { type: 'table_plan' });
        window.location.href = data.checkout_url;
      }
    },
  });
}

// Derived hook — true when the current team can use table plans
export function useCanUseTablePlans(): boolean | undefined {
  const { data } = useBillingUsage();
  if (!data) return undefined;   // still loading
  return data.table_plan_enabled;
}

// Sync subscription from Stripe (call after checkout redirect)
export function useSyncSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params?: { invite_id?: number }) => {
      const { data } = await apiClient.post<{ subscribed: boolean; synced: boolean; invite_paid?: boolean }>('/api/v1/billing/sync', params);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

export function useTransferPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourceId, targetId }: { sourceId: number; targetId: number }) => {
      const { data } = await apiClient.post<{ message: string }>(`/api/v1/invites/${sourceId}/transfer-payment`, { target_invite_id: targetId });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

// Purchase a pack of email credits (one-time payment)
export function usePurchaseCredits() {
  return useMutation({
    mutationFn: async (inviteId?: number) => {
      const { data } = await apiClient.post<CheckoutResponse>('/api/v1/billing/purchase-credits', {
        invite_id: inviteId,
      });
      return data;
    },
  });
}

// Verify a Stripe checkout session (call after returning from checkout for one-time payments)
export function useVerifyPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const { data } = await apiClient.post<{ status: string; product?: string }>('/api/v1/billing/verify-payment', { session_id: sessionId });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}
